import type { AeroSectionSolveRow } from "../../api/aeroWorkbench";
import type { SurfaceCpElement } from "../highlift/foil/FlowVisLayer";

const SHAPE_SAMPLE_COUNT = 33;

export interface SectionDiagnosticTarget {
  airfoil: string;
  alphaDeg: number;
  reynolds: number;
  mach: number;
}

export interface CampaignSectionMatch {
  row: AeroSectionSolveRow;
  distance: number;
  shapeRms: number;
  alphaDeltaDeg: number;
  reynoldsRatio: number;
  machDelta: number;
  samePoint: boolean;
}

/**
 * Match the backend's sampled-camber-thickness.v1 representation for NACA-like
 * slider sections. This is used only to choose a nearby piece of evidence; the
 * backend's content-addressed vector remains authoritative.
 */
export function naca4EvidenceVector(airfoil: string): number[] {
  const code = airfoil.match(/(\d{4})/)?.[1] ?? "0012";
  const camber = Number(code[0]) / 100;
  const position = Number(code[1]) / 10;
  const thickness = Number(code.slice(2)) / 100;
  const camberValues: number[] = [];
  const thicknessValues: number[] = [];

  for (let index = 0; index < SHAPE_SAMPLE_COUNT; index += 1) {
    const x = 0.5 * (1 - Math.cos(Math.PI * index / (SHAPE_SAMPLE_COUNT - 1)));
    let yc = 0;
    if (camber !== 0 && position > 0 && position < 1) {
      if (x < position) {
        yc = camber / (position * position) * (2 * position * x - x * x);
      } else {
        const aft = 1 - position;
        yc = camber / (aft * aft) * ((1 - 2 * position) + 2 * position * x - x * x);
      }
    }
    const halfThickness = 5 * thickness * (
      0.2969 * Math.sqrt(x)
      - 0.1260 * x
      - 0.3516 * x ** 2
      + 0.2843 * x ** 3
      - 0.1036 * x ** 4
    );
    camberValues.push(yc);
    thicknessValues.push(Math.max(0, 2 * halfThickness));
  }
  return [...camberValues, ...thicknessValues];
}

function finiteNumber(record: Record<string, unknown> | undefined, key: string): number | null {
  const value = record?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function exactCoefficientRow(row: AeroSectionSolveRow): boolean {
  return row.status === "done"
    && row.prediction_receipt?.exact === true
    && row.prediction?.converged === true
    && finiteNumber(row.prediction, "cl") != null
    && finiteNumber(row.prediction, "cd") != null
    && Array.isArray(row.shape_vector)
    && row.shape_vector.length === SHAPE_SAMPLE_COUNT * 2
    && row.shape_vector.every(Number.isFinite)
    && finiteNumber(row.query, "alpha_eff_deg") != null
    && finiteNumber(row.query, "reynolds") != null
    && finiteNumber(row.query, "mach") != null;
}

/** Pick the nearest exact campaign observation without implying it is identical. */
export function nearestExactCampaignSection(
  rows: readonly AeroSectionSolveRow[],
  target: SectionDiagnosticTarget,
): CampaignSectionMatch | null {
  const targetShape = naca4EvidenceVector(target.airfoil);
  let nearest: CampaignSectionMatch | null = null;

  for (const row of rows) {
    if (!exactCoefficientRow(row)) continue;
    const shape = row.shape_vector!;
    const shapeRms = Math.sqrt(shape.reduce((sum, value, index) => (
      sum + (value - targetShape[index]!) ** 2
    ), 0) / shape.length);
    const alpha = finiteNumber(row.query, "alpha_eff_deg")!;
    const reynolds = finiteNumber(row.query, "reynolds")!;
    const mach = finiteNumber(row.query, "mach")!;
    const alphaDeltaDeg = alpha - target.alphaDeg;
    const reynoldsRatio = reynolds / target.reynolds;
    const machDelta = mach - target.mach;
    // Scales mirror the campaign's declared correlation-length priors. This is
    // a display-side nearest-neighbour score, never an aerodynamic prediction.
    const distance = Math.sqrt(
      (shapeRms / 0.02) ** 2
      + (alphaDeltaDeg / 2) ** 2
      + (Math.log10(reynoldsRatio) / 0.5) ** 2
      + (machDelta / 0.05) ** 2,
    );
    const candidate: CampaignSectionMatch = {
      row,
      distance,
      shapeRms,
      alphaDeltaDeg,
      reynoldsRatio,
      machDelta,
      samePoint: shapeRms < 1e-10
        && Math.abs(alphaDeltaDeg) < 1e-9
        && Math.abs(Math.log(reynoldsRatio)) < 1e-9
        && Math.abs(machDelta) < 1e-9,
    };
    if (!nearest || candidate.distance < nearest.distance) nearest = candidate;
  }
  return nearest;
}

function finiteCpSamples(value: unknown): Array<[number, number]> {
  if (!Array.isArray(value)) return [];
  return value.flatMap((sample): Array<[number, number]> => {
    if (Array.isArray(sample) && sample.length >= 2) {
      const x = Number(sample[0]);
      const cp = Number(sample[1]);
      return Number.isFinite(x) && Number.isFinite(cp) ? [[x, cp]] : [];
    }
    if (sample && typeof sample === "object") {
      const record = sample as Record<string, unknown>;
      const x = Number(record.x_over_c ?? record.x);
      const cp = Number(record.cp);
      return Number.isFinite(x) && Number.isFinite(cp) ? [[x, cp]] : [];
    }
    return [];
  });
}

/** Read only the explicit solver-native Cp contract; never synthesize a trace. */
export function solverNativeCp(row: AeroSectionSolveRow | null | undefined): SurfaceCpElement[] {
  const raw = row?.raw;
  if (!raw) return [];
  const contract = raw.surface_cp;
  let samples: Array<[number, number]> = [];
  if (contract && typeof contract === "object" && !Array.isArray(contract)) {
    const record = contract as Record<string, unknown>;
    if (record.evidence_status !== "solver-native") return [];
    samples = finiteCpSamples(record.samples);
  }
  if (samples.length < 3) return [];

  let leadingEdge = 0;
  for (let index = 1; index < samples.length; index += 1) {
    if (samples[index]![0] < samples[leadingEdge]![0]) leadingEdge = index;
  }
  const byX = (left: [number, number], right: [number, number]) => left[0] - right[0];
  const upper = samples.slice(0, leadingEdge + 1).reverse().sort(byX);
  const lower = samples.slice(leadingEdge).sort(byX);
  if (upper.length < 2 || lower.length < 2) return [];
  return [{ element: "section", upper, lower }];
}
